import { useEffect, useMemo } from 'react';

import { useQuery } from '@apollo/client/react';
import {
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { Link2 } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import { RaLabel } from '@modules/_shared/components/form/RaLabel';
import { formatAddress } from '@modules/_shared/utils/formatting';
import { useLocale } from '@shared/hooks/locale';
import { toGlobalId } from '@shared/utils/global-id';

import {
  type PickerState,
  pickerState,
  reconciledParentIds,
} from './filingState';

// The CRM users behind the addresses typed in the composer, with their recent
// leads. agent and tenant_admin only: the `user` role has no select on
// `emails`, the caller skips the picker for it. Capped at 10 contacts on
// purpose: a mail to more than ten CRM contacts is a mass mail's job, and
// the ones past the cap are simply not offered (filed nowhere).
export const GET_COMPOSER_CONTACT_MATCHES = gql(/* GraphQL */ `
  query GetComposerContactMatches($emails: [citext!]!) {
    users(
      where: { emails: { email: { _in: $emails } } }
      order_by: { created_at: asc }
      limit: 10
    ) {
      id
      full_name
      emails(where: { email: { _in: $emails } }) {
        email
      }
      leads(order_by: { created_at: desc }, limit: 5) {
        id
        created_at
        property {
          route
          street_number
          postcode
          locality
        }
      }
    }
  }
`);

const NONE = '';
const NO_MATCHES: never[] = [];

// Asks the agent where a mail written from the mailbox is filed when its
// addresses match CRM contacts: on the contact, on one of their leads, or
// nowhere. One match preselects the contact; several leave the choice open
// (the service then files nothing rather than guessing). The value is the
// list of activity parents, as global ids, that the send request carries.
export const AttachToPicker = ({
  emails,
  value,
  onChange,
  decided,
  onContactDecided,
  onStateChange,
}: {
  emails: string[];
  value: string[];
  onChange: (parentGlobalIds: string[]) => void;
  // Contacts the agent already answered for, "Do not attach" included: an
  // empty value is not the same as no decision, and a later query result
  // must not preselect what was explicitly refused. Held by the host so it
  // survives the composer moving to the dialog with its draft.
  decided: string[];
  onContactDecided: (userId: string) => void;
  // Where the lookup for the CURRENT addresses stands. `loading`: the agent
  // has had no chance to decide, and the host must not tell the service that
  // they did. `ready`: the choice on screen is confirmed against those
  // addresses. `unavailable`: the lookup failed, so nothing here can be
  // trusted about the current recipients — the host keeps the agent's choice
  // (it may come back) but must not send it.
  onStateChange?: (state: PickerState) => void;
}) => {
  const { t, locale } = useLocale();
  const normalized = useMemo(
    () => [...new Set(emails.map(email => email.trim().toLowerCase()))],
    [emails],
  );
  const { data, previousData, loading, error } = useQuery(
    GET_COMPOSER_CONTACT_MATCHES,
    {
      variables: { emails: normalized },
      skip: normalized.length === 0,
    },
  );
  // The rule, and why each branch is what it is, lives in `filingState.ts`
  // with its tests.
  const state = pickerState({
    loading,
    hasData: data != null,
    hasError: error != null,
  });
  useEffect(() => {
    onStateChange?.(state);
    // The callback is the host's setter, stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);
  // While the next address list loads, the previous matches stay, so the
  // agent's choice is not wiped for the duration of a refetch.
  const matches = data?.users ?? previousData?.users ?? NO_MATCHES;
  // One contact behind the addresses: filed on them unless the agent says
  // otherwise. A parent chosen for a contact that left the recipients goes.
  useEffect(() => {
    // Only a confirmed match set prunes. Before the matches land `matches` is
    // empty, and after a failed lookup it is the previous address list's: in
    // both cases a parent would be dropped, or kept, on evidence that does not
    // describe the current recipients. The draft keeps the agent's choice
    // either way; what the host sends is gated on the state, not on this.
    if (state !== 'ready') {
      return;
    }
    const next = reconciledParentIds({
      matches: matches.map(user => ({
        userId: user.id,
        parents: [
          toGlobalId('User', user.id),
          ...user.leads.map(lead => toGlobalId('Lead', lead.id)),
        ],
      })),
      value,
      decided,
    });
    if (next != null) {
      onChange(next);
    }
    // `value` is read, not depended on: this only reacts to the matches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches, state]);

  if (state === 'unavailable') {
    // Whatever `previousData` still holds describes other addresses: showing
    // it as a live choice is what would mislead. The agent can still send, and
    // the caption says what that means for the filing.
    return (
      <Typography variant="caption" color="text.secondary">
        {t('Could not load the contacts for these addresses')}
      </Typography>
    );
  }

  if (matches.length === 0) {
    return null;
  }

  return (
    <Stack sx={{ gap: 1 }}>
      {matches.map(user => {
        const userParent = toGlobalId('User', user.id);
        const leadParents = user.leads.map(lead => toGlobalId('Lead', lead.id));
        const selected =
          value.find(id => id === userParent || leadParents.includes(id)) ??
          NONE;
        return (
          <FormControl key={user.id} size="small" fullWidth>
            <RaLabel
              label={`${t('Attach to')} · ${
                user.full_name ?? user.emails[0]?.email ?? ''
              }`}
            />
            <Select
              // Outlined on paper like the composer's other fields (RaSelect).
              variant="outlined"
              size="small"
              sx={{ background: 'var(--mui-palette-background-paper)' }}
              value={selected}
              displayEmpty
              startAdornment={
                <Link2 size={16} style={{ marginRight: 8, opacity: 0.6 }} />
              }
              onChange={event => {
                const next = String(event.target.value);
                onContactDecided(user.id);
                onChange([
                  ...value.filter(
                    id => id !== userParent && !leadParents.includes(id),
                  ),
                  ...(next === NONE ? [] : [next]),
                ]);
              }}
            >
              <MenuItem value={NONE}>
                <Typography color="text.secondary">
                  {t('Do not attach')}
                </Typography>
              </MenuItem>
              <MenuItem value={userParent}>
                {t('Contact')} · {user.full_name ?? user.emails[0]?.email}
              </MenuItem>
              {user.leads.map(lead => (
                <MenuItem key={lead.id} value={toGlobalId('Lead', lead.id)}>
                  {t('Lead')} ·{' '}
                  {lead.property != null
                    ? formatAddress(lead.property, ', ', true)
                    : ''}{' '}
                  · {new Date(lead.created_at).toLocaleDateString(locale)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      })}
    </Stack>
  );
};
